import React from 'react'

import Paper from '@mui/material/Paper'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'

import { CustomInsights } from '../../types/CustomInsight'

import CreatedRowCell from './cells/CreatedRowCell'
import GeneratedRowCell from './cells/GeneratedRowCell'
import TitleRowCell from './cells/TitleRowCell'
import StatusLinkRowCell from './cells/StatusLinkRowCell'
import StatusRowCell from './cells/StatusRowCell'

import { copy } from '../../Copy'

interface TableProps {
  copy: typeof copy
  insights: CustomInsights
}

export const Table: React.FC<TableProps> = ({ copy, insights }) => {
  const rows = React.useMemo(
    () =>
      insights.map((insight, idx) => ({
        id: idx,
        ...insight,
      })),
    [insights],
  )

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      minWidth: 246,
      flex: 1,
      renderCell: (params) => <TitleRowCell {...params} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      headerAlign: 'center',
      maxWidth: 168,
      minWidth: 126,
      flex: 1,
      renderCell: (params) => <StatusRowCell copy={copy} {...params} />,
    },
    {
      field: 'final_generated_count',
      headerName: 'Generated',
      sortable: false,
      headerAlign: 'center',
      width: 168,
      renderCell: (params) => <GeneratedRowCell {...params} />,
    },
    {
      field: 'created_date',
      headerName: 'Created',
      sortable: false,
      width: 196,
      renderCell: (params) => <CreatedRowCell {...params} />,
    },
    {
      field: 'button',
      headerName: '',
      maxWidth: 124,
      minWidth: 82,
      sortable: false,
      renderCell: (params) => <StatusLinkRowCell copy={copy} {...params} />,
    },
  ]

  return (
    <Paper
      sx={{
        border: '1px solid var(--divider, rgba(0, 0, 0, 0.12))',
        boxShadow: 'none',
        width: '100%',
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu={true}
        hideFooter={true}
        hideFooterPagination={true}
        hideFooterRowCount={true}
        hideFooterSelectedRowCount={true}
        initialState={{
          sorting: {
            sortModel: [{ field: 'title', sort: 'desc' }],
          },
        }}
        sx={{
          '& .MuiBadge-standard': {
            display: 'none',
          },
          '& .MuiDataGrid-filler': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            fontSize: 13,
            p: 16,
            whiteSpace: 'nowrap',
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'unset',
          },
          '& .MuiDataGrid-columnHeader': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
            opacity: '1 !important',
            visibility: 'visible !important',
          },
          '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon': {
            opacity: '1 !important',
          },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
            visibility: 'visible !important',
            width: 'auto',
          },
          '& .MuiDataGrid-sortButton': {
            opacity: '1 !important',
            visibility: 'visible !important',
          },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton': {
            opacity: '1 !important',
            visibility: 'visible !important',
          },
          backgroundColor: 'transparent',
        }}
      />
    </Paper>
  )
}
